import {
  Component,
  Input,
  Output,
  EventEmitter as AngularEventEmitter,
  ViewChild,
} from '@angular/core';
import { EnvVariables } from '../../environments/environment';
import * as debug from 'debug';
import * as _ from 'lodash';
import * as $ from 'jquery';
import * as turf from '@turf/turf';
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import { EventEmitter } from '../../includes/eventEmitter';
import { GeolocationProvider } from '../../providers/geolocation/geolocation';
import { ToolsProvider } from '../../providers/tools/tools';
import { cleanCoords } from '@turf/turf';
import { TripService } from '../../providers/trip-service/trip-service';
import { PlaceServiceProvider } from '../../providers/trip-service/place-service';
import { FormatGasPricePipe } from '../../pipes/format-gas-price/format-gas-price';
import { Platform } from 'ionic-angular';
import * as moment from 'moment';

let activeMap;
let mostRecentMap;

@Component({
  selector: 'map',
  templateUrl: 'map.html',
})
export class MapComponent extends EventEmitter {
  log = debug('app:MapComponent');

  // Static Variables
  //   - we use these because we don't want mapboxgl to fire up
  //     all over again everytime you come back. We'll save this
  //     in the background and reinsert.
  static staticMapDiv;
  static staticMap;

  // Ins and Outs
  @Input()
  config: any;
  @Input()
  initImmediately = false;

  // deprecated
  @Input()
  opts: any;

  @Output()
  load = new AngularEventEmitter();
  @Output()
  activeMarker = new AngularEventEmitter();
  @Output()
  onDrag = new AngularEventEmitter();
  @Output()
  onZoom = new AngularEventEmitter();
  @Output() mapClick = new AngularEventEmitter();

  // Vars
  @ViewChild('container')
  mapContainer;
  map: any;
  inited = false;
  loaded = false;
  waiting = false;
  errorMessage = '';
  isAndroidError = '';
  isntAndroidError = '';
  markers = [];
  markerData = [];
  userLocationMarker;
  storeActiveMarker;
  uniqueId = _.uniqueId();
  placesMode = 'all';
  @Input() style = 'NAVIGATION';
  styles = {
    NAVIGATION: 'mapbox://styles/cdllife/ck8ypuc5n03rw1it34su7yn10',
    SATELLITE: 'mapbox://styles/cdllife/ck8ypn60j03k11hpcvj7vxg29',
  };

  events: any[] = [];
  mapId;
  hasBeenDisplayed;

  // geolocation stuff
  isAndroid = false;

  constructor(
    public tools: ToolsProvider,
    private env: EnvVariables,
    public geolocation: GeolocationProvider,
    public tripService: TripService,
    public placeService: PlaceServiceProvider,
    public plt: Platform
  ) {
    super();

    this.mapId = _.uniqueId();
    mostRecentMap = this.mapId;

    mapboxgl.accessToken = this.env.mapboxglAccessToken;
    if (activeMap) {
      try {
        activeMap.clearMap();
      } catch (e) {}
    }
    activeMap = window['activeMap'] = this;
    this.log('map_layers | constructed', this.uniqueId);
    this.isAndroid = this.plt.is('android');
  }

  ngOnInit() {
    this.log('map_layers | inited', this.uniqueId);
    this.clearMap();
    if (this.initImmediately) {
      setTimeout(() => {
        this.initMapComponent();
      });
    }

    setTimeout(() => {
      this.waiting = true;
    }, 750);

    setTimeout(() => {
      this.displayErrorMessages();
    }, 5000);

    this.events.push(
      this.tripService.on('placesMode', placesMode => {
        this.placesMode = placesMode;
      })
    );

    // reInitMap
    this.events.push(
      this.geolocation.on('updateUserLocation', () => {
        this.updateUserLocationMarker();
      })
    );

    // set resize events
    this.events.push(
      this.tools.on('viewDidEnter', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('viewWillEnter', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('onKeyboardShow', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('onKeyboardHide', () => {
        this.resizeMapTimeouts();
      })
    );

    this.events.push(
      this.tools.on('orientationChange', () => {
        this.resizeMapTimeouts();
      })
    );
  }

  ngOnDestroy(): void {
    this.log('map_layers | destroy', this.uniqueId);
    if (activeMap === this) {
      this.clearMap();
    } else {
      this.log('map_layers | skipping destroy');
    }
    _.each(this.events, e => {
      e();
    });
  }

  displayErrorMessages() {
    this.errorMessage =
      'Uh Oh! It appears you have denied us access to your location.';
    this.isAndroidError =
      'To solve this, please go to the "Settings" App, click "Apps", scroll to CDLLife, click that, and then turn on location services. Once complete, you may need to restart the app.';
    this.isntAndroidError =
      'To solve this, please go to the "Settings" App, scroll to CDLLife, click that, and then turn on location services. Once complete, you may need to restart the app.';
  }

  reconnectMap() {
    try {
      if (activeMap && activeMap !== this) {
        try {
          activeMap.clearMap();
        } catch (e) {}
      }
      if (MapComponent.staticMapDiv) {
        this.mapContainer.nativeElement.append(MapComponent.staticMapDiv);
        _.set(this.map, 'activeComponent', this);
        activeMap = this;
      }
    } catch (e) {}
  }

  isActiveMapComponent() {
    return _.get(this.map, 'activeComponent') === this;
  }

  private initializeExistingMap() {
    this.log('initializing existing map');

    this.mapContainer.nativeElement.append(MapComponent.staticMapDiv);
    this.map = MapComponent.staticMap;
    this.hasBeenDisplayed = true;
    this.clearMarkers();

    _.set(this.map, 'activeComponent', this);

    this.geolocation.getRecentPosition().then(res => {
      this.map.setCenter([
        res.longitude || -94.582178,
        res.latitude || 39.09052,
      ]);
    });

    this.log('map_layers | initializeExistingMap');

    if (this.map.onStyleData) {
      this.map.off('styledata', this.map.onStyleData);
    }

    this.map.once(
      'styledata',
      (this.map.onStyleData = _.debounce(e => {
        const ac = MapComponent.staticMap.activeComponent;
        if (ac === this) {
          this.addBaseSources();
          this.load.emit();
          this.loaded = true;
        }
      }, 250))
    );

    this.map.setStyle(this.styles[this.style]);
    this.clearMap();
    this.addUsersLocation();

    setTimeout(() => {
      this.map.resize();
    });
  }

  addBaseSources() {
    try {
      if (!this.map.getSource('allCDLLifePlaces')) {
        this.map.addSource('allCDLLifePlaces', {
          type: 'vector',
          tiles: [`${this.env.mappingAPI}/api/tiles/places/{z}/{x}/{y}.pbf`],
        });
      }
    } catch (e) {}
  }

  changeStyle(s?): Promise<any> {
    let url = this.styles[s];

    if (url) {
      this.style = s;
    } else {
      this.style = 'NAVIGATION';
      url = this.styles[this.style];
    }

    return new Promise<void>((resolve, reject) => {
      this.log('changing style', this.style, url);

      this.map.setStyle(url);

      this.map.once('styledata', () => {
        this.addBaseSources();
        resolve();
      });
    });
  }

  initMapComponent() {
    // just in case :)
    if (this.inited) {
      _.set(this.map, 'activeComponent', this);
      return;
    }

    this.inited = true;

    // This Sill prevent the map flashing cause it won't need to reload
    if (MapComponent.staticMap) {
      return this.initializeExistingMap();
    }

    this.geolocation.getRecentPosition().then(res => {
      let mapDiv = (MapComponent.staticMapDiv = document.createElement('div')),
        defaultMapConfigs = {
          style: this.styles[this.style],
          center: [res.longitude || -94.575522, res.latitude || 39.0997],
          zoom: 9,
          pitch: 0,
          minZoom: 1,
          maxZoom: 19,
          container: mapDiv,
          sprite: 'mapbox://sprites/cdllife/ck1wiu4ea3ufc1cmklyn486r',
        };

      this.mapContainer.nativeElement.append(mapDiv);

      this.log('config overrides', this.config);

      window['_map'] = this.map;

      this.map = MapComponent.staticMap = window['_map'] = new mapboxgl.Map(
        _.merge(defaultMapConfigs, this.config)
      );

      this.map.activeComponent = this;

      this.map.on(
        'zoom',
        _.debounce(() => {
          MapComponent.staticMap.activeComponent.onZoom.emit();
        }, 400)
      );

      this.map.on(
        'move',
        _.debounce(event => {
          MapComponent.staticMap.activeComponent.onDrag.emit({
            from: this.map.lastCenter,
            to: (this.map.lastCenter = this.map.getCenter()),
          });
        }, 400)
      );

      this.map.on('dragstart', () => {
        this.emit('showButtons', false);
      });

      this.map.on(
        'dragend',
        _.debounce(() => {
          this.emit('showButtons', true);
        }, 500)
      );

      this.map.on('click', e => {
        try {
          const query = this.map.queryRenderedFeatures(e.point);
          _.each(query, q => {
            const l = _.get(q, 'layer.id');
            MapComponent.staticMap.activeComponent.emit(
              `click.${l}`,
              q.properties
            );
          });
        } catch (e) {}
      });

      this.centerMap();

      this.map.on('load', () => {
        this.addBaseSources();
        this.hasBeenDisplayed = true;

        const ac = MapComponent.staticMap.activeComponent;

        // who cares which parent
        ac.load.emit();
        ac.addUsersLocation();

        this.loaded = true;
      });

      let clickedPlaces;
      this.map.on('click', 'ALL_PLACES', () => {
        clickedPlaces = true;
        setTimeout(() => {
          clickedPlaces = false;
        }, 150);
      });

      $(mapDiv).click(e => {
        setTimeout(() => {
          if (clickedPlaces) {
            return;
          }
          if ($(e.target).hasClass('MapMarker')) {
            return;
          }
          MapComponent.staticMap.activeComponent.mapClick.emit();
          MapComponent.staticMap.activeComponent.inactivateMarker();
        });
      });
    });
  }

  clearMap() {
    this.clearMarkers();

    this.log('map_layers | clearing map');

    if (this.userLocationMarker) {
      this.userLocationMarker.remove();
    }

    // we should probably register layers and markers added,
    // not hardcode names here
    this.nullSourceData('routes');
    this.nullSourceData('waypoints');
    $('.mapboxgl-marker').remove();
    try {
      this.map.setLayoutProperty('ALL_PLACES', 'visibility', 'none');
    } catch (e) {}
  }

  removeLayer(layer) {
    if (!this.map) {
      return;
    }
    const l = this.map.getLayer(layer);
    if (l) {
      this.log('map_layers | removing layer', layer, this.uniqueId);
      this.map.removeLayer(layer);
    }
  }

  getCenter() {
    this.log('getting a center');
    return this.map.getCenter();
  }

  private nullSourceData(source) {
    try {
      const s = this.map.getSource(source);
      s.setData({
        type: 'FeatureCollection',
        features: [],
      });
    } catch (e) {}
  }

  removeSource(source) {
    if (!this.map) {
      return;
    }
    const s = this.map.getSource(source);
    if (s) {
      this.log('map_layers | removing source', source, this.uniqueId);
      this.map.removeSource(source);
    }
  }

  // I (darrin) don't think this is called
  reload() {
    if (this.map) {
      this.map.resize();
    }
  }

  flyToAndZoomIn(coords, zoomLevel?, speed?) {
    this.map.flyTo({
      center: coords,
      zoom: zoomLevel || 16,
      speed: speed || 0.75,
    });
  }

  centerOnUsersLocation(z?, opts?) {
    this.addUsersLocation().then(res => {
      const flyTo: any = {
        center: [res.longitude, res.latitude],
        pitch: 0,
        duration: 450,
      };
      if (z) {
        flyTo.zoom = z;
      }
      this.log('centering on user', flyTo, opts);
      this.map.flyTo(_.merge(flyTo, opts));
    });
  }

  addUsersLocation() {
    const span = document.createElement('span');

    return this.geolocation.getRecentPosition().then(
      res => {
        this.log('got users location', res);

        if (this.userLocationMarker) {
          this.userLocationMarker.remove();
        }

        span.innerHTML = `
          <i class="fas fa-map-pin fa-2x" text-color="blue-light"></i>
        `;

        this.userLocationMarker = new mapboxgl.Marker({
          element: span,
          anchor: 'bottom',
        })
          .setLngLat([res.longitude, res.latitude])
          .addTo(this.map);

        $(this.userLocationMarker._element).css('z-index', 1);

        return res;
      },
      e => {
        this.log(e);
      }
    );
  }

  redrawMarkers() {
    const data = this.markerData;
    this.clearMarkers();
    this.updateMarkers(data);
  }

  getZoom() {
    return this.map.getZoom();
  }
  getMapRadius() {
    const mapBounds = this.map.getBounds(),
      ne = turf.point(_.toArray(_.get(mapBounds, '_ne'))),
      sw = turf.point(_.toArray(_.get(mapBounds, '_sw')));
    const diameter = turf.distance(ne, sw, { units: 'meters' });
    return diameter / 2;
  }

  updateMarkers(feature, zoomToFit = true, places = [], opts?) {
    // places is deprecated as an option

    opts = _.merge(
      {
        zoomIncludeCurrentPosition: true,
      },
      opts
    );
    this.clearMarkers();
    this.log('updateMarkers: ', feature);
    const coordinates = [];

    _.each(feature, feature => {
      let markerElement = document.createElement('span'),
        markerImage = _.get(feature, 'properties.userImage'),
        markerIcon = _.get(
          feature,
          'properties.icon',
          'fal fa-user-circle' // fallback for static
        ),
        type = _.get(feature, 'properties.type');

      markerElement.classList.add('MapMarker');
      markerElement.classList.add(
        'MapMarker-' + _.get(feature.properties, '_id')
      );

      const price = _.get(feature, 'properties.info.gasPrices.price');
      let mode = this.placesMode;

      if (markerImage) {
        markerElement.innerHTML = `
          <div class="MapMarker-imageWrapper">
            <img src="${markerImage}" />
          </div>
        `;
      } else if (mode === 'fuelStopPreference') {
        let p = price || 'N/A';

        let str = String(p).replace(/[^0-9.]/g, ''),
          d = Number(str) || 0,
          b = d < 0 ? '-$' : '$';

        if (d <= 0) {
          p = 'N/A';
        } else {
          p =
            '$' +
            d
              .toFixed(2)
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
              .replace(/\.00$/, '');
        }
        if (markerIcon !== 'fal fa-user-circle') {
          markerElement.innerHTML += `
                <span class="MapMarker-label" style="display: flex; align-items: center; line-height: 2">
                  <i class="${markerIcon}" style="margin-right: 4px"></i> ${p}
                </span>
              `;
        } else {
          markerElement.innerHTML += `
                <span class="MapMarker-label" style="display: flex; align-items: center; line-height: 2">
                 ${p}
                </span>
              `;
        }
      } else {
        markerElement.innerHTML = `
          <i class="MapMarker-icon ${markerIcon}"></i>
        `;
      }

      markerElement.addEventListener('click', event => {
        this.log('CLICKED PLACE', event);
        if (this.storeActiveMarker) {
          $(this.storeActiveMarker).removeClass('is-active');
        }

        $(markerElement).addClass('is-active');
        this.storeActiveMarker = markerElement;

        Object.defineProperty(feature, '_markerElement', {
          value: markerElement,
          writable: false,
          enumerable: false,
        });
        this.activeMarker.emit(feature);
        this.emit('activeMarker', feature);
        event.preventDefault();
        event.stopPropagation();
      });

      this.markerData.push(feature);
      const m = new mapboxgl.Marker({
        element: markerElement,
        anchor: 'bottom',
      })
        .setLngLat(feature.geometry.coordinates)
        .addTo(this.map);

      m._feature = feature;
      this.markers.push(m);
    });

    if (zoomToFit) {
      this.centerMap(opts.zoomIncludeCurrentPosition);
    }
  }

  activateMarker(markerIndex) {
    // this.log('markerData: ', markerIndex);
    // this.log('markers: ', this.markers);
    // this.log('marker: ', this.markers[markerIndex]);
    // // this.markers[markerIndex]._state = 'active';
    // // $(this.markers[markerIndex]).addClass('is-active');
    // $(this.markers[markerIndex]).addClass('is-active');
  }

  inactivateMarker() {
    $(this.storeActiveMarker).removeClass('is-active');
    this.activeMarker.emit(null);
    this.storeActiveMarker = null;
  }

  clearMarkers() {
    this.inactivateMarker();
    if (_.size(this.markers)) {
      _.each(this.markers, m => {
        m.remove();
      });
    }

    this.markers = [];
  }

  redrawLayers(layers) {
    this.map.redrawLayers();
  }

  resizeMapTimeouts() {
    if (this.map) {
      this.map.resize();
      setTimeout(() => {
        this.map && this.map.resize();
      }, 10);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 20);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 500);
      setTimeout(() => {
        this.map && this.map.resize();
      }, 2000);
    }
  }

  resizeMap() {
    this.map.resize();
  }

  centerMap(includeCurrentPosition = true) {
    let bounds = new mapboxgl.LngLatBounds();
    this.log('center map markers: ', bounds);
    this.markerData.forEach(marker => {
      bounds.extend(marker.geometry.coordinates);
    });
    if (includeCurrentPosition) {
      this.geolocation.getRecentPosition().then(
        res => {
          if (res && res.latitude && res.longitude) {
            bounds.extend([res.longitude, res.latitude]);
          }
          this.map.fitBounds(bounds, { padding: 80, maxZoom: 9 });
        },
        e => {
          this.map.fitBounds(bounds, { padding: 80 });
        }
      );
    } else {
      this.map.fitBounds(bounds, { padding: 80, maxZoom: 9 });
    }
  }

  fitBounds(bounds, options = {}) {
    this.map.fitBounds(bounds, options);
  }

  updateUserLocationMarker() {
    this.geolocation.getRecentPosition().then(loc => {
      this.log('need to update marker', loc);

      if (!this.userLocationMarker) {
        return this.log('no marker');
      }
      this.userLocationMarker.setLngLat([loc.longitude, loc.latitude]);
    });
  }

  isMostRecentMap() {
    return mostRecentMap === this.mapId;
  }

  openAppSettings() {
    try {
      const settings = _.get(window, 'cordova.plugins.settings');
      settings.open(
        'application_details',
        function () {
          // console.log('opened settings');
        },
        function () {
          // console.log('failed to open settings');
        }
      );
    } catch (e) {}
  }
}



// WEBPACK FOOTER //
// ./src/components/map/map.ts